import { Stack, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { endOfDay, isValid, startOfDay } from "date-fns";
import { useTranslation } from "react-i18next";
import { testIds } from "./EffectiveDatesFields.testIds";

export type EffectiveDatesPatch = {
  effectiveStartDate?: Date | null;
  effectiveEndDate?: Date | null;
};

type EffectiveDatesFieldsProps = {
  effectiveStartDate?: string | Date | null;
  effectiveEndDate?: string | Date | null;
  onChange: (patch: EffectiveDatesPatch) => void;
  startTestId?: string;
  endTestId?: string;
};

// Show both fields at 00:00. Giving the picker back the stored 23:59 end date
// made it rewrite the date while the user was still typing it.
const toPickerDate = (value?: string | Date | null): Date | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return isValid(date) ? startOfDay(date) : null;
};

/**
 * Effective Dates picker shared by billing rules and rate tables (TTMSD-1959).
 * Dates are datetime under the hood: the start snaps to 00:00 and the end to
 * 23:59 of the picked day. Leaving both blank means the rate is always in effect.
 */
const EffectiveDatesFields = ({
  effectiveStartDate,
  effectiveEndDate,
  onChange,
  startTestId = testIds.effectiveDatesStart,
  endTestId = testIds.effectiveDatesEnd,
}: EffectiveDatesFieldsProps) => {
  const { t } = useTranslation("billingRules");
  const start = toPickerDate(effectiveStartDate);
  const end = toPickerDate(effectiveEndDate);

  // Skip half typed dates so an invalid date never reaches the form.
  const emitDay = (
    field: keyof EffectiveDatesPatch,
    date: unknown,
    snap: (day: Date) => Date
  ) => {
    if (!date) {
      onChange({ [field]: null });
      return;
    }
    if (!isValid(date as Date)) {
      return;
    }
    onChange({ [field]: snap(date as Date) });
  };

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="primary" fontWeight="bold">
        {t("form.effectiveDates", "Effective Dates")}
      </Typography>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <DatePicker
          label={t("form.effectiveStart", "Start")}
          value={start}
          onChange={(date) => emitDay("effectiveStartDate", date, startOfDay)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              inputProps={{ ...params.inputProps, "data-testid": startTestId }}
            />
          )}
        />
        <DatePicker
          label={t("form.effectiveEnd", "End")}
          value={end}
          minDate={start ?? undefined}
          onChange={(date) => emitDay("effectiveEndDate", date, endOfDay)}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              inputProps={{ ...params.inputProps, "data-testid": endTestId }}
            />
          )}
        />
      </Stack>
    </Stack>
  );
};

export default EffectiveDatesFields;
